import React, { useState, useEffect, useRef } from 'react';
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  Grid, 
  Box,
  Typography,
  Divider,
  Chip,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BILLABLE_ACTIVITY_STATUSES, ACTIVITY_TYPES, SALES_ORDER_STATUSES } from '../Services/billingService';

const BillingFilter = ({ 
  initialFilters = {}, 
  onFilterChange, // Remove default value here
  isVisible = true, 
  onClose 
}) => {
    // State to match the fields for billing activities
    const [filters, setFilters] = useState({
        // Entity filters
        patient_id: initialFilters.patient_id || '',
        patient_name: initialFilters.patient_name || '',
        doctor_id: initialFilters.doctor_id || '',
        doctor_name: initialFilters.doctor_name || '',
        center_id: initialFilters.center_id || '',
        center_name: initialFilters.center_name || '',
        payer_id: initialFilters.payer_id || '',
        payer_name: initialFilters.payer_name || '',
        
        // Date filters
        performed_at_from: initialFilters.performed_at_from || null,
        performed_at_to: initialFilters.performed_at_to || null,
        created_at_from: initialFilters.created_at_from || null,
        created_at_to: initialFilters.created_at_to || null,
        
        // Activity filters
        activity_type: initialFilters.activity_type || '',
        description: initialFilters.description || '',
        status: initialFilters.status || '',
        
        // Monetary filters
        min_amount: initialFilters.min_amount || '',
        max_amount: initialFilters.max_amount || '',
        has_pending_amount: initialFilters.has_pending_amount || '',
        
        // Authorization filters
        has_authorization: initialFilters.has_authorization || '',
        authorization_code: initialFilters.authorization_code || '',
        
        // Sales order filters
        has_sales_orders: initialFilters.has_sales_orders || '',
        so_status: initialFilters.so_status || '',
        
        // Billing status filters
        is_fully_invoiced: initialFilters.is_fully_invoiced || '',
        is_partially_invoiced: initialFilters.is_partially_invoiced || '',
        is_non_billable: initialFilters.is_non_billable || '',
        
        // Search
        search: initialFilters.search || ''
    });

    // Options for dropdowns
    const statusOptions = Object.entries(BILLABLE_ACTIVITY_STATUSES).map(([key, value]) => ({
        value,
        label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase().replace(/_/g, ' ')
    }));

    const activityTypeOptions = Object.entries(ACTIVITY_TYPES).map(([key, value]) => ({
        value,
        label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()
    }));

    const salesOrderStatusOptions = Object.entries(SALES_ORDER_STATUSES).map(([key, value]) => ({
        value,
        label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase().replace(/_/g, ' ')
    }));

    const booleanOptions = [
        { value: '', label: 'All' },
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' }
    ];

    // Sync filters with initialFilters when they change
    useEffect(() => {
        setFilters({
            patient_id: initialFilters.patient_id || '',
            patient_name: initialFilters.patient_name || '',
            doctor_id: initialFilters.doctor_id || '',
            doctor_name: initialFilters.doctor_name || '',
            center_id: initialFilters.center_id || '',
            center_name: initialFilters.center_name || '',
            payer_id: initialFilters.payer_id || '',
            payer_name: initialFilters.payer_name || '',
            performed_at_from: initialFilters.performed_at_from || null,
            performed_at_to: initialFilters.performed_at_to || null,
            created_at_from: initialFilters.created_at_from || null,
            created_at_to: initialFilters.created_at_to || null,
            activity_type: initialFilters.activity_type || '',
            description: initialFilters.description || '',
            status: initialFilters.status || '',
            min_amount: initialFilters.min_amount || '',
            max_amount: initialFilters.max_amount || '',
            has_pending_amount: initialFilters.has_pending_amount || '',
            has_authorization: initialFilters.has_authorization || '',
            authorization_code: initialFilters.authorization_code || '',
            has_sales_orders: initialFilters.has_sales_orders || '',
            so_status: initialFilters.so_status || '',
            is_fully_invoiced: initialFilters.is_fully_invoiced || '',
            is_partially_invoiced: initialFilters.is_partially_invoiced || '',
            is_non_billable: initialFilters.is_non_billable || '',
            search: initialFilters.search || ''
        });
    }, [initialFilters]);

    // Helper function to safely call onFilterChange
    const callOnFilterChange = (newFilters) => {
        if (onFilterChange && typeof onFilterChange === 'function') {
            onFilterChange(newFilters);
        }
    };

    const handleInputChange = (name, value) => {
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);
        callOnFilterChange(newFilters);
    };

    const handleDateChange = (name, date) => {
        const newFilters = { ...filters, [name]: date };
        setFilters(newFilters);
        callOnFilterChange(newFilters);
    };

    const resetFilters = () => {
        const resetState = {
            patient_id: '',
            patient_name: '',
            doctor_id: '',
            doctor_name: '',
            center_id: '',
            center_name: '',
            payer_id: '',
            payer_name: '',
            performed_at_from: null,
            performed_at_to: null,
            created_at_from: null,
            created_at_to: null,
            activity_type: '',
            description: '',
            status: '',
            min_amount: '',
            max_amount: '',
            has_pending_amount: '',
            has_authorization: '',
            authorization_code: '',
            has_sales_orders: '',
            so_status: '',
            is_fully_invoiced: '',
            is_partially_invoiced: '',
            is_non_billable: '',
            search: ''
        };

        setFilters(resetState);
        callOnFilterChange(resetState);
    };

    // Count active filters
    const activeFiltersCount = Object.values(filters).filter(value => 
        value !== '' && value !== null && value !== undefined
    ).length;

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant="h6">Billing Activity Filters</Typography>
                        {activeFiltersCount > 0 && (
                            <Chip 
                                label={`${activeFiltersCount} active`} 
                                size="small" 
                                color="primary" 
                                sx={{ mt: 0.5 }}
                            />
                        )}
                    </Box>
                    {onClose && (
                        <Button 
                            onClick={onClose}
                            sx={{ minWidth: 'auto', p: 1 }}
                        >
                            ×
                        </Button>
                    )}
                </Box>

                <Box sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {/* Entity Filters */}
                    <Typography variant="subtitle2" gutterBottom>
                        Entities
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Patient Name"
                                value={filters.patient_name}
                                onChange={(e) => handleInputChange('patient_name', e.target.value)}
                                placeholder="Search by patient name"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Patient ID"
                                value={filters.patient_id}
                                onChange={(e) => handleInputChange('patient_id', e.target.value)}
                                placeholder="Filter by patient ID"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Doctor Name"
                                value={filters.doctor_name}
                                onChange={(e) => handleInputChange('doctor_name', e.target.value)}
                                placeholder="Search by doctor name"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Doctor ID"
                                value={filters.doctor_id}
                                onChange={(e) => handleInputChange('doctor_id', e.target.value)}
                                placeholder="Filter by doctor ID"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Center Name"
                                value={filters.center_name}
                                onChange={(e) => handleInputChange('center_name', e.target.value)}
                                placeholder="Search by center name"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Payer Name"
                                value={filters.payer_name}
                                onChange={(e) => handleInputChange('payer_name', e.target.value)}
                                placeholder="Search by payer name"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Date Filters */}
                    <Typography variant="subtitle2" gutterBottom>
                        Dates
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="Performed From"
                                value={filters.performed_at_from}
                                onChange={(date) => handleDateChange('performed_at_from', date)}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="Performed To"
                                value={filters.performed_at_to}
                                onChange={(date) => handleDateChange('performed_at_to', date)}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="Created From"
                                value={filters.created_at_from}
                                onChange={(date) => handleDateChange('created_at_from', date)}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label="Created To"
                                value={filters.created_at_to}
                                onChange={(date) => handleDateChange('created_at_to', date)}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Activity Details */}
                    <Typography variant="subtitle2" gutterBottom>
                        Activity Details
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Activity Type</InputLabel>
                                <Select
                                    value={filters.activity_type}
                                    onChange={(e) => handleInputChange('activity_type', e.target.value)}
                                    label="Activity Type"
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {activityTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    {statusOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Description"
                                value={filters.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder="Search in description"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Monetary Filters */}
                    <Typography variant="subtitle2" gutterBottom>
                        Amounts
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Min Amount"
                                value={filters.min_amount}
                                onChange={(e) => handleInputChange('min_amount', e.target.value)}
                                placeholder="Minimum amount"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Max Amount"
                                value={filters.max_amount}
                                onChange={(e) => handleInputChange('max_amount', e.target.value)}
                                placeholder="Maximum amount"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Has Pending Amount</InputLabel>
                                <Select
                                    value={filters.has_pending_amount}
                                    onChange={(e) => handleInputChange('has_pending_amount', e.target.value)}
                                    label="Has Pending Amount"
                                >
                                    {booleanOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Authorization */}
                    <Typography variant="subtitle2" gutterBottom>
                        Authorization
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Has Authorization</InputLabel>
                                <Select
                                    value={filters.has_authorization}
                                    onChange={(e) => handleInputChange('has_authorization', e.target.value)}
                                    label="Has Authorization"
                                >
                                    {booleanOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Authorization Code"
                                value={filters.authorization_code}
                                onChange={(e) => handleInputChange('authorization_code', e.target.value)}
                                placeholder="Search by auth code"
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Sales Orders & Billing Status */}
                    <Typography variant="subtitle2" gutterBottom>
                        Sales Orders & Billing
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Has Sales Orders</InputLabel>
                                <Select
                                    value={filters.has_sales_orders}
                                    onChange={(e) => handleInputChange('has_sales_orders', e.target.value)}
                                    label="Has Sales Orders"
                                >
                                    {booleanOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sales Order Status</InputLabel>
                                <Select
                                    value={filters.so_status}
                                    onChange={(e) => handleInputChange('so_status', e.target.value)}
                                    label="Sales Order Status"
                                >
                                    <MenuItem value="">All SO Statuses</MenuItem>
                                    {salesOrderStatusOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Fully Invoiced</InputLabel>
                                <Select
                                    value={filters.is_fully_invoiced}
                                    onChange={(e) => handleInputChange('is_fully_invoiced', e.target.value)}
                                    label="Fully Invoiced"
                                >
                                    {booleanOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Partially Invoiced</InputLabel>
                                <Select
                                    value={filters.is_partially_invoiced}
                                    onChange={(e) => handleInputChange('is_partially_invoiced', e.target.value)}
                                    label="Partially Invoiced"
                                >
                                    {booleanOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Non-Billable</InputLabel>
                                <Select
                                    value={filters.is_non_billable}
                                    onChange={(e) => handleInputChange('is_non_billable', e.target.value)}
                                    label="Non-Billable"
                                >
                                    {booleanOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
                        <Button 
                            variant="outlined"
                            onClick={resetFilters}
                        >
                            Clear Filters
                        </Button>
                        {onClose && (
                            <Button 
                                variant="contained"
                                onClick={onClose}
                            >
                                Apply Filters
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default BillingFilter;